import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { jobSourcesApi } from '../../services/api';
import { AtsPlatform, BoardReadResult, SourceBoard } from '../../types';
import { errorMessage, fieldErrors } from '../../utils/errors';
import { atsLabel } from '../../utils/jobBoard';
import { ATS_PLATFORMS, TOKEN_EXAMPLES, isValidToken, looksLikeUrl, parseBoardUrl } from '../../utils/jobSources';

interface Props {
  open: boolean;
  onClose: () => void;
  onAdded: (board: SourceBoard, result: BoardReadResult | null) => void;
}

const TITLE_ID = 'add-board-title';
const NOT_A_BOARD_LINK = 'This is not a Greenhouse, Lever, Ashby or Workable board link.';

/**
 * Add a company's board to the registry: the ATS and the board token, typed
 * or taken from a pasted posting link. The API reads the board at once, so a
 * token the ATS does not know is reported here and nothing is kept.
 */
const AddBoardDialog: React.FC<Props> = ({ open, onClose, onAdded }) => {
  const [link, setLink] = useState('');
  const [platform, setPlatform] = useState<AtsPlatform | ''>('');
  const [token, setToken] = useState('');
  const [name, setName] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLink('');
    setPlatform('');
    setToken('');
    setName('');
    setErrors({});
    setFormError('');
    setSaving(false);
  }, [open]);

  const clearError = (field: string) =>
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });

  const found = parseBoardUrl(link);
  const linkProblem = link.trim() !== '' && !found;

  const changeLink = (value: string) => {
    setLink(value);
    const parsed = parseBoardUrl(value);
    if (parsed) {
      setPlatform(parsed.platform);
      setToken(parsed.token);
      setErrors({});
    }
  };

  // A link pasted into the token field is understood too.
  const changeToken = (value: string) => {
    clearError('token');
    const parsed = looksLikeUrl(value) ? parseBoardUrl(value) : null;
    if (parsed) {
      setPlatform(parsed.platform);
      setToken(parsed.token);
      setLink(value.trim());
      clearError('platform');
    } else {
      setToken(value);
    }
  };

  const submit = async () => {
    if (saving) return;
    const cleanToken = token.trim();
    const problems: Record<string, string> = {};
    if (!platform) problems.platform = 'Choose the ATS the board is on.';
    if (!cleanToken) {
      problems.token = 'Enter the board token, or paste a link above.';
    } else if (looksLikeUrl(cleanToken)) {
      problems.token = NOT_A_BOARD_LINK;
    } else if (!isValidToken(cleanToken)) {
      problems.token = 'A token is letters, digits, dots, dashes and underscores only, with no spaces.';
    }
    if (Object.keys(problems).length || !platform) {
      setErrors(problems);
      return;
    }

    setSaving(true);
    setErrors({});
    setFormError('');
    try {
      const cleanName = name.trim();
      const { board, result } = await jobSourcesApi.addBoard({
        platform,
        token: cleanToken,
        ...(cleanName ? { name: cleanName } : {}),
      });
      onAdded(board, result ?? null);
    } catch (e) {
      const byField = fieldErrors(e);
      if (byField.token || byField.platform || byField.name) {
        setErrors(byField);
      } else {
        setFormError(errorMessage(e, 'Could not add the board.'));
      }
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby={TITLE_ID}>
      <DialogTitle id={TITLE_ID}>Add a board</DialogTitle>
      <DialogContent>
        <form
          id="add-board-form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <Typography variant="body2" color="text.secondary">
            A board is one company's careers page on an ATS. Its token is the company's name in the link to any of its
            postings:
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, mt: 1, mb: 2 }}>
            {TOKEN_EXAMPLES.map((example) => (
              <Typography component="li" variant="body2" key={example.platform} sx={{ overflowWrap: 'anywhere' }}>
                {atsLabel(example.platform)}:{' '}
                <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                  {example.before}
                  <Box component="strong" sx={{ bgcolor: 'action.selected', px: 0.25, borderRadius: 0.5 }}>
                    {example.token}
                  </Box>
                  {example.after}
                </Box>
              </Typography>
            ))}
          </Box>

          <Stack spacing={2}>
            <TextField
              label="Paste a posting or board link (optional)"
              placeholder="https://jobs.lever.co/acme/..."
              value={link}
              onChange={(e) => changeLink(e.target.value)}
              error={linkProblem}
              helperText={
                found
                  ? `Found the ${atsLabel(found.platform)} board "${found.token}".`
                  : linkProblem
                  ? `${NOT_A_BOARD_LINK} You can still fill in the fields below.`
                  : 'The ATS and the token are read from the link.'
              }
              size="small"
              fullWidth
              autoFocus
              disabled={saving}
              inputProps={{ inputMode: 'url', autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                required
                label="ATS"
                value={platform}
                onChange={(e) => {
                  setPlatform(e.target.value as AtsPlatform);
                  clearError('platform');
                }}
                error={Boolean(errors.platform)}
                helperText={errors.platform}
                size="small"
                disabled={saving}
                sx={{ minWidth: { sm: 170 } }}
              >
                {ATS_PLATFORMS.map((value) => (
                  <MenuItem key={value} value={value}>
                    {atsLabel(value)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                required
                label="Board token"
                placeholder="acme"
                value={token}
                onChange={(e) => changeToken(e.target.value)}
                error={Boolean(errors.token)}
                helperText={errors.token}
                size="small"
                fullWidth
                disabled={saving}
                inputProps={{ autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false, maxLength: 400 }}
              />
            </Stack>
            <TextField
              label="Company name (optional)"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                clearError('name');
              }}
              error={Boolean(errors.name)}
              helperText={errors.name || 'Left empty, the name the ATS gives is used.'}
              size="small"
              fullWidth
              disabled={saving}
              inputProps={{ maxLength: 255 }}
            />
          </Stack>

          {formError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formError}
            </Alert>
          )}
          {saving && (
            <Typography variant="body2" color="text.secondary" role="status" sx={{ mt: 2 }}>
              Reading the board. A large one can take up to about 20 seconds.
            </Typography>
          )}
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" form="add-board-form" variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} color="inherit" aria-label="Adding" /> : 'Add board'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddBoardDialog;
